import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar la solicitud');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-lg shadow-lg p-6 md:p-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Eliminación de Cuenta</h1>
          <p className="text-gray-500 mb-8">NotiMax - Gestión de Citas por WhatsApp</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Cómo solicitar la eliminación de tu cuenta</h2>
              <p className="mb-3">Si deseas eliminar tu cuenta de NotiMax y todos los datos asociados, puedes hacerlo de las siguientes formas:</p>
              <ol className="list-decimal pl-6 space-y-3">
                <li>
                  <strong>Desde la aplicación:</strong> Inicia sesión en tu cuenta, ve a tu perfil y selecciona la opción "Eliminar cuenta".
                </li>
                <li>
                  <strong>Por correo electrónico:</strong> Envía un correo a <a href="mailto:soporte@notimax.app" className="text-blue-600 hover:underline">soporte@notimax.app</a> con el asunto "Solicitud de eliminación de cuenta" indicando el correo electrónico asociado a tu cuenta.
                </li>
                <li>
                  <strong>Desde esta página:</strong> Completa el formulario que aparece más abajo.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Datos que se eliminan</h2>
              <p className="mb-3">Al eliminar tu cuenta, se borrarán de forma permanente los siguientes datos:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Tu información personal (nombre, correo electrónico, contraseña)</li>
                <li>Todas tus citas registradas</li>
                <li>Datos de pacientes asociados a tus citas</li>
                <li>Notas y recomendaciones creadas</li>
                <li>Configuración de sucursales y servicios</li>
                <li>Etiquetas personalizadas</li>
                <li>Perfil profesional</li>
                <li>Sesiones de dispositivos activas</li>
                <li>Información de suscripción</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Datos que se conservan</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Registros de transacciones de pago:</strong> Se conservan por un período de 5 años por obligaciones fiscales y legales. Estos registros son anonimizados y no contienen información personal identificable.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Plazo de procesamiento</h2>
              <p>Las solicitudes de eliminación se procesan en un plazo máximo de <strong>7 días hábiles</strong>. Recibirás una confirmación por correo electrónico una vez que la eliminación se haya completado.</p>
            </section>

            {/* Deletion Request Form */}
            <section className="pt-4 border-t">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Solicitar eliminación de cuenta</h2>

              {submitted ? (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800">Solicitud recibida</p>
                        <p className="text-green-700 mt-1">
                          Hemos recibido tu solicitud de eliminación de cuenta para <strong>{email}</strong>.
                          Procesaremos tu solicitud en un plazo máximo de 7 días hábiles y te notificaremos por correo electrónico.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                          Esta acción es irreversible. Al solicitar la eliminación de tu cuenta, todos tus datos serán borrados permanentemente.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Correo electrónico de tu cuenta *
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      data-testid="delete-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo (opcional)
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="¿Por qué deseas eliminar tu cuenta?"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      data-testid="delete-reason-input"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !email}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="delete-submit-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {loading ? 'Enviando...' : 'Solicitar eliminación de cuenta'}
                  </Button>
                </form>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
